import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { band, type ResultRow } from '../lib/results';
import { SKILLS, SKILL_LABEL, gatherResults, type SkillBand } from '../lib/trf';
import { PageHeader } from '../components/PageHeader';
import { RevealBand } from '../components/RevealBand';
import { ProgressLine } from '../components/ProgressLine';
import { Burst } from '../components/Burst';
import { personalBest, progressPoints } from '../lib/progress';
import { firstSight } from '../lib/first-sight';
import { Banner } from '../components/Banner';

/** One skill: its band, or why there is not one yet. */
function SkillTile({ label, skill }: { label: string; skill: SkillBand }) {
  const body =
    skill.state === 'marked' ? (
      <span className="trf__band trf__band--small">{band(skill.band)}</span>
    ) : (
      <span className="trf__na">N/A</span>
    );

  const why =
    skill.state === 'awaiting'
      ? 'with your teacher'
      : skill.state === 'not-offered'
        ? 'not set here'
        : skill.state === 'not-sat'
          ? 'not sat'
          : null;

  const inner = (
    <>
      <p className="trf__label">{label}</p>
      {body}
      {why && <p className="trf__why">{why}</p>}
    </>
  );

  // Only a marked paper has answers worth opening.
  return skill.resultId && skill.state === 'marked' ? (
    <Link className="trf__tile trf__tile--link" to={`/my/results/${skill.resultId}`}>
      {inner}
      <span className="trf__chev" aria-hidden="true">›</span>
    </Link>
  ) : (
    <article className="trf__tile">{inner}</article>
  );
}

export function StudentResultsPage() {
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * The tests whose overall this student is seeing for the first time — the
   * ones whose band arrives rather than sits there. Worked out once, as the
   * results load, and keyed by the band too: a band that changes after a
   * Writing script is marked is news again.
   */
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  /** Whether the progress line is new to this student, so it draws itself. */
  const [freshLine, setFreshLine] = useState(false);

  useEffect(() => {
    api
      .get<{ results: ResultRow[] }>('/results')
      .then(({ data }) => {
        setRows(data.results);
        const points = progressPoints(gatherResults(data.results));
        setFreshLine(
          points.length >= 2 && firstSight(`progress:${points.map((p) => p.overall).join(',')}`),
        );
        setFresh(
          new Set(
            gatherResults(data.results)
              .filter((test) => test.overall !== null && firstSight(`band:${test.name}:${test.overall}`))
              .map((test) => test.name),
          ),
        );
      })
      .catch((err) => setError(errorMessage(err, 'Could not load your results')))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="My tests"
        title="My results"
        meta={<p className="muted">Results appear once your teacher releases them.</p>}
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--step-1)' }}>
              No results yet
            </p>
            <p className="muted">
              A paper you have sat will appear here once your teacher releases the marks.
            </p>
          </div>
        ) : (
          <div className="stack">
            <ProgressLine points={progressPoints(gatherResults(rows))} fresh={freshLine} />
            {gatherResults(rows).map((test) => (
              <section key={test.name} className={`trf${fresh.has(test.name) ? ' trf--fresh' : ''}`}>
                <header className="trf__head">
                  <h2 className="trf__name">{test.name}</h2>
                  {test.awaiting && (
                    <p className="muted">A paper is still with your teacher to mark.</p>
                  )}
                </header>

                <article className="trf__overall">
                  <p className="trf__label">
                    Overall
                    {personalBest(gatherResults(rows)) === test.name && (
                      <span className="trf__best">New personal best</span>
                    )}
                  </p>
                  {/* The burst comes as the count lands, the first time only. */}
                  {fresh.has(test.name) && personalBest(gatherResults(rows)) === test.name && (
                    <Burst delay={1050} />
                  )}
                  <RevealBand
                    className="trf__band"
                    value={test.overall}
                    fresh={fresh.has(test.name)}
                  />
                </article>

                <div className="trf__grid">
                  {SKILLS.map((skill) => (
                    <SkillTile key={skill} label={SKILL_LABEL[skill]} skill={test.skills[skill]} />
                  ))}
                </div>

                {/*
                  * Said plainly rather than left to be worked out. A real
                  * overall is the mean of four skills and this platform sets
                  * three, so the number above is not one — and a candidate
                  * deciding whether to book the real test on the strength of it
                  * should know that before they do.
                  */}
                <p className="trf__note">
                  Practice only. The overall is the mean of the papers you have sat here, rounded as
                  the real test rounds it — not an IELTS band score, and this platform does not test
                  Speaking.
                </p>
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
